import { Model } from '@viddsee/server-api-client';
import sourceGetter from '@/scripts/libs/player/sourceGetter';
import { VideoSourceResponse } from '@/scripts/libs/player/sourceGenerator';
import { VideoStoreActionContext } from './initializer';
import { VideoLoadingError } from '../state/errors';
import { SetVideoPlayerErrorPayload } from '../mutations/setVideoPlayerError';
import { assertNotNull, createPlayerErrorWithIssuer } from '../helpers/helper';
import analytic from '@/scripts/libs/analytic';

export interface GetVideoSourcePayload {
  video: Model.Video.Info;
  token: string;
}
export interface GetVideoSourceOptions {
  /**
   * @default false
   */
  forceHosted?: boolean;
}

const asLoadingError = createPlayerErrorWithIssuer(VideoLoadingError);

const setVideoLoadingError = (context: VideoStoreActionContext, payload: SetVideoPlayerErrorPayload | null): void => {
  context.commit('setVideoPlayerError', asLoadingError(payload));
};

const setSource = (context: VideoStoreActionContext, video: Model.Video.Info, response: VideoSourceResponse): void => {
  const generatedVideoSourcePayload = sourceGetter.generateAVideoPlaylistItem(video)(response);
  context.commit('setVideoSource', generatedVideoSourcePayload);

  setVideoLoadingError(context, null);
};

interface ObtainVideoSourceOptions {
  shouldForceHosted?: boolean;
}

export const obtainVideoSource = async(context: VideoStoreActionContext, options: ObtainVideoSourceOptions = {}): Promise<void> => {
  const token = assertNotNull(context.state.token);
  const video = assertNotNull(context.state.video);

  try {
    const sourceResponse = await sourceGetter.obtainVideoSource({
      uid: video.id,
      token,
      source: 'web',
    }, !!options.shouldForceHosted);

    setSource(context, video, sourceResponse);
  } catch (error) {
    setVideoLoadingError(context, {
      error: new VideoLoadingError(error.message),
    });
  }
};

interface FallbackOnVideoSourcePayload {
  error: Error;
}

export const fallbackOnVideoSource = async(context: VideoStoreActionContext, payload: FallbackOnVideoSourcePayload): Promise<void> => {
  const payloadErrorMessage = payload.error.message;

  // Cache and remove current video source
  const existingVideoSource = context.state.videoSource;
  context.commit('setVideoSource', null);

  try {
    // Attempt to reinitialize video player with new source
    const newVideoSource = await sourceGetter.obtainFallbackVideoSource(payloadErrorMessage, existingVideoSource);
    if (newVideoSource) {
      setSource(context, assertNotNull(context.state.video), newVideoSource);
      return;
    }

    // No fallback video source is available. Request hosted source instead.
    await obtainVideoSource(context, {
      shouldForceHosted: true,
    });
  } catch (error) {
    setVideoLoadingError(context, {
      error,
      customMessage: payloadErrorMessage,
    });
  }
};
